import type { ReactNode } from 'react'
import { SlotIcon } from '../../icons/SlotIcon.js'
import { type DossierView, dossierView, readableBlockName } from './dossierValue.js'
import type { VerwijzingProps } from './kinds.js'

/**
 * Een alleen-lezen waarde uit het dossier (R-54, R-58, R-61, R-63).
 *
 * **Waarom dit geen invoerveld is dat op slot staat.** R-58 vraagt dat een overgenomen waarde
 * zichtbaar herkenbaar is als niet-bewerkbaar, met de bron erbij. Een `input` met `readonly`
 * ziet eruit als een veld dat je zou moeten kunnen vullen en dat het even niet doet; dit is
 * iets anders, namelijk een gegeven van elders. Daarom staat er tekst en geen invoervak, en
 * daarom is `bewerkbaar` hier geen prop die iets doet: een verwijzing is nooit bewerkbaar, in
 * geen enkele weergave en voor geen enkele rol (R-56).
 *
 * **De stand onbeschikbaar is een uitkomst en geen fout.** R-61 zegt dat een veld waarvan de
 * bron leeg of niet toegankelijk is dát toont in plaats van leeg te blijven, en dat is precies
 * het geval waarin de gastheer het document niet heeft meegegeven (besluit 4 van M6.0). De
 * invuller leest dan dat er een bron is en dat er niets uit komt. **Waaróm er niets uit komt
 * staat er niet, en dat is geen slordigheid maar R-63**: het verschil tussen "dat blok heeft
 * niets achtergelaten" en "er staat iets maar jij mag het niet zien" is zelf informatie over een
 * ander bouwblok. De uitkomst die deze control binnenkrijgt draagt die reden ook niet; hij
 * bestaat alleen in het log van de pod.
 *
 * **De bron erbij, en dat is de bloknaam en niet het pad.** R-58 vraagt de bron, en wat een
 * invuller daarmee kan is weten waar het vandaan komt: "uit de call". Het pad binnen het
 * document (`call.budget`) is inrichting en zegt hem niets; het staat wel in de configurator bij
 * het kiezen (D-38). Zolang de registry geen leesbaar label per blok aanlevert is de bloknaam
 * het beste wat er is, en dat is een gat in de contracten en niet in deze control.
 *
 * **De "Alleen-lezen"-pil (d2, R-157) komt naast de brontekst, niet in plaats ervan.** De pil is
 * het visuele signaal dat R-58 vraagt; de brontekst erna blijft staan, want die draagt andere
 * informatie (welk bouwblok) die een pil niet kan dragen.
 *
 * **Elke vorm die een veld in een blokdocument kan hebben, en niet alleen tekst.** Tot het
 * beoordelingsformulier meer velden uit de aanvraag ging tonen stond hier alleen een tekst, een
 * getal of een ja/nee; trefwoorden, rich text met meer alinea's en bijlagen stonden er als "Niet
 * beschikbaar" terwijl de waarde er was. Wat er nu van welke vorm op het scherm komt, en wat de
 * pod daarvoor niet weet, staat in `dossierwaarde.ts`. De wortel is daarom een `div` en geen `p`:
 * een lijst of een reeks alinea's past niet in een alinea.
 */
export function Verwijzing({ veld: field, taal: language, koppeling: link, uitkomst: outcome, bron: source, bronNaam: sourceName, inBronGroep: inGroup = false }: VerwijzingProps): ReactNode {
  // De weergave van het bronveld (R-55, R-100): wat de aanvrager zag. Een tabel hoort bij
  // `Subgrid`; hier telt alleen de weergave van één waarde.
  const sourceField = outcome.stand === 'waarde' && outcome.weergave?.soort !== 'tabel' ? outcome.weergave : undefined
  const view = outcome.stand === 'waarde' ? dossierView(outcome.waarde, language, sourceField) : undefined
  // Eén alinea staat in de regel naast de pil; een lijst of meer alinea's krijgt een eigen blok
  // eronder, want een opsomming midden in een regel leest niet.
  const inline = view === undefined || (view.soort === 'tekst' && view.alineas.length === 1)
  return (
    <div
      className="ef-verwijzing"
      id={link.id}
      aria-describedby={link['aria-describedby']}
      data-stand={view === undefined ? 'onbeschikbaar' : 'waarde'}
      data-proef={`verwijzing-${field.id}`}
    >
      {/* De waarde eerst en de herkomst klein erachter (ontwerp van het invulscherm): wat de lezer
          zoekt is het gegeven, de markering zegt waar het vandaan komt (R-58). */}
      {inline ? (
        <span className="ef-verwijzing__waarde">
          {view?.soort === 'tekst' ? view.alineas[0] : language === 'nl' ? 'Niet beschikbaar' : 'Not available'}
        </span>
      ) : null}
      {inGroup ? null : (
        <span className="ef-verwijzing__badge">
          <SlotIcon aria-hidden="true" />
          {language === 'nl' ? 'Alleen-lezen' : 'Read-only'}
        </span>
      )}
      {source === undefined || inGroup ? null : (
        // Klein naast de waarde en zonder haakjes, met de naam van het blok waar die bekend is
        // (besluit van 29 oktober 2026): "uit Call publiceren" en niet "(uit call-publiceren)".
        <span className="ef-verwijzing__bron">
          {' '}
          {language === 'nl' ? 'uit' : 'from'} {sourceName ?? readableBlockName(source)}
        </span>
      )}
      {inline || view === undefined ? null : <Block weergave={view} />}
    </div>
  )
}

/**
 * De waarde als blok: alinea's van rich text, of een lijst trefwoorden, keuzes of bijlagen.
 *
 * Alleen tekstknopen, nooit HTML: de platte tekst van rich text is niet ontsnapt
 * (`dossierwaarde.ts`), dus wat hier als opmaak lijkt is tekst en blijft dat.
 */
function Block({ weergave: view }: { readonly weergave: DossierView }): ReactNode {
  if (view.soort === 'tekst') {
    return (
      <div className="ef-verwijzing__waarde ef-verwijzing__blok">
        {view.alineas.map((paragraph, index) => (
          // De plek is de identiteit: een bevroren tekst die niet wordt herschikt.
          // eslint-disable-next-line react/no-array-index-key
          <p key={index}>{paragraph}</p>
        ))}
      </div>
    )
  }
  return (
    <ul className="ef-verwijzing__waarde ef-verwijzing__blok ef-verwijzing__lijst">
      {view.items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>{item}</li>
      ))}
    </ul>
  )
}
